import { Dialog, Button, Typography } from "@material-tailwind/react";
import { t } from "i18next";
import { PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { splitOdooTransferDuration } from "../../services/odoo/workorderOdoo";

interface SendToOdooDialogProps {
  open: boolean;
  workorderRef: string;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
  estimatedSeconds?: number | null;
}

const LoadingDots = () => (
  <div className="flex gap-1 items-center">
    <div className="w-1.5 h-1.5 bg-current rounded-full animate-[bounce_1s_infinite_0ms]"></div>
    <div className="w-1.5 h-1.5 bg-current rounded-full animate-[bounce_1s_infinite_200ms]"></div>
    <div className="w-1.5 h-1.5 bg-current rounded-full animate-[bounce_1s_infinite_400ms]"></div>
  </div>
);

const transferEstimateLabel = (estimatedSeconds: number): string => {
  const { count, unit } = splitOdooTransferDuration(estimatedSeconds);
  const durationKey =
    unit === "seconds"
      ? "workorder:odoo:estimatedTransferSeconds"
      : "workorder:odoo:estimatedTransferMinutes";

  return t("workorder:odoo:estimatedTransferTime", {
    duration: t(durationKey, { count }),
  });
};

export default function SendToOdooDialog({
  open,
  workorderRef,
  onClose,
  onConfirm,
  loading = false,
  estimatedSeconds = null,
}: SendToOdooDialogProps) {

  // Empêcher la fermeture si en cours de chargement
  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      handler={handleClose} 
      size="xs"
      dismiss={{
        enabled: !loading,
        outsidePress: !loading,
        escapeKey: !loading
      }}
    >
      <div className="p-4">
        <div className="flex items-center gap-3 mb-2">
          <div
            className="p-2 rounded-full"
            style={{ backgroundColor: "#E5980D" }} // Couleur identique à l'icône PaperAirplaneIcon
          >
            <PaperAirplaneIcon className="h-5 w-5 text-white -rotate-90" />
          </div>
          <Typography variant="h5">
            {t("workorder:action:sendToOdoo")} - #{workorderRef}
          </Typography>
        </div>
        <Typography className={loading && estimatedSeconds != null ? "mb-2" : "mb-4"}>
          {t("workorder:odoo:confirmSendText")}
        </Typography>
        {loading && estimatedSeconds != null && (
          <Typography className="mb-4 font-medium" style={{ color: "#DC2626" }}>
            {transferEstimateLabel(estimatedSeconds)}
          </Typography>
        )}
        <div className="flex justify-end gap-2">
          <Button
            onClick={handleClose}
            variant="outlined"
            color="blue-gray"
            size="sm"
            disabled={loading}
          >
            {t("Annuler")}
          </Button>
          <Button 
            style={{ backgroundColor: "#E5980D" }}
            onClick={onConfirm}
            size="sm"
            disabled={loading}
            className="flex items-center justify-center min-w-[100px]"
          >
            {loading ? <LoadingDots /> : t("Confirmer")}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
